import { ReactNode, useCallback, useMemo, useReducer, useState } from 'react';
import {
  CreateOwnerContractContext,
  CreateOwnerContractContextValue,
} from './CreateOwnerContractContext';
import { CreateOwnerContractData } from '@/custom-types';
import { OwnerContractLeaseTypeOption, PaymentFrequencyOption } from '@/graphql/generated';
import { createOwnerContractReducer } from '@/reducers';

type OwnerContractCreationProviderProps = {
  children: ReactNode;
};

const INITIAL_DATA: CreateOwnerContractData = {
  ownerId: '',
  ownerName: '',
  propertyId: '',
  propertyName: '',
  insuranceId: '',
  paymentFrequencyOption: PaymentFrequencyOption.Monthly,
  contractAmount: '0',
  startDate: new Date().toISOString(),
  finishDate: new Date().toISOString(),
  ownerContractLeaseTypeOption: OwnerContractLeaseTypeOption.OccupationManagment,

};

export const CreateOwnerContractContextProvider = ({ children }: OwnerContractCreationProviderProps) => {
  const [state, dispatch] = useReducer(createOwnerContractReducer, INITIAL_DATA);
  const [isDisabled, setIsDisabled] = useState(false);

  const setOwner = useCallback((ownerId: string, ownerName: string) => {
    dispatch({ type: 'OWNER_CONTRACT-setOwner', payload: { ownerId, ownerName } });
  }, []);

  const setProperty = useCallback((propertyId: string, propertyName: string) => {
    dispatch({ type: 'OWNER_CONTRACT-setProperty', payload: { propertyId, propertyName } });
  }, []);

  const setLeaseType = useCallback((leaseType: OwnerContractLeaseTypeOption) => {
    dispatch({ type: 'OWNER_CONTRACT-setLeaseType', payload: { leaseType } });
  }, []);

  const setFrequencyPayment = useCallback((frequencyPayment: PaymentFrequencyOption) => {
    dispatch({ type: 'OWNER_CONTRACT-setFrequencyPayment', payload: { frequencyPayment } });
  }, []);

  const setInsuranceId = useCallback((insuranceId: string | null) => {
    dispatch({ type: 'OWNER_CONTRACT-setInsuranceId', payload: { insuranceId } });
  }, []);

  const setContractAmount = useCallback((amount: string | null) => {
    if (amount) dispatch({ type: 'OWNER_CONTRACT-setContractAmount', payload: { amount }});
  }, []);

  const setDates = useCallback((startDate: string | null, finishDate: string | null) => {
    if (startDate && finishDate) dispatch({ type: 'OWNER_CONTRACT-setDates', payload: { startDate, finishDate } });
  }, []);

  const setButtonDisabled = useCallback(() => setIsDisabled(true), [])
  const setButtonEnabled = useCallback(() => setIsDisabled(false), [])

  const value = useMemo<CreateOwnerContractContextValue>(
    () => ({
      state,
      setOwner,
      setProperty,
      setLeaseType,
      setInsuranceId,
      setContractAmount,
      setFrequencyPayment,
      setDates,
      setButtonDisabled,
      setButtonEnabled,
      buttonState: isDisabled,
    }),
    [
      state,
      setOwner,
      setProperty,
      setLeaseType,
      setInsuranceId,
      setContractAmount,
      setFrequencyPayment,
      setDates,
      setButtonDisabled,
      setButtonEnabled,
      isDisabled,
    ],
  );

  return (
    <CreateOwnerContractContext.Provider value={value}>
      {children}
    </CreateOwnerContractContext.Provider>
  );
};
